// src/components/Layout/Sidebar.jsx
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Upload,
  Download,
  Database,
  Shield,
  PackagePlus,
  X,
  ChevronLeft,
  Menu,
  HardDrive,
  ChevronDown,
  ChevronRight,
  Globe,
  FileCheck,
} from 'lucide-react';
import { useState } from 'react';
import useStore from '../../store/useStore';
import enactLogo from '../../assets/LOGOTIPO-1@2x.png';

// Full navigation definition with access control metadata
const allNavigationSections = [
  {
    name: 'General',
    roles: null, // visible to everyone
    items: [
      { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', roles: null },
      { name: 'Contracts', icon: FileCheck, path: '/contracts', roles: null },
      // Data & Objects Store is admin-only
      { name: 'Data & Objects Store', icon: HardDrive, path: '/data-store', roles: ['admin', 'realm-admin'] },
    ]
  },
  {
    name: 'Provider',
    roles: ['data-provider', 'admin', 'realm-admin'], // realm-admin = Keycloak client role
    items: [
      { name: 'Provider Dashboard', icon: Upload, path: '/provider-dashboard', roles: null },
      { name: 'Policies', icon: Shield, path: '/policies', roles: null },
      { name: 'Assets', icon: Database, path: '/assets', roles: null },
      { name: 'Data Offers', icon: PackagePlus, path: '/provider-data-exchange', roles: null },
    ]
  },
  {
    name: 'Consumer',
    roles: ['data-consumer', 'admin', 'realm-admin'],
    items: [
      { name: 'Consumer Dashboard', icon: Download, path: '/consumer-dashboard', roles: null },
      { name: 'Data Pool', icon: Globe, path: '/data-pool', roles: null },
    ]
  }
];

function hasRole(userRoles, allowedRoles) {
  if (!allowedRoles) return true;
  return allowedRoles.some(r => userRoles.includes(r));
}

export default function Sidebar({ open, setOpen }) {
  const user = useStore((state) => state.user);
  const userRoles = user?.roles || [];

  const [expandedSections, setExpandedSections] = useState({
    'General': true,
    'Provider': true,
    'Consumer': true
  });

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Filter sections and items based on user roles
  const visibleSections = allNavigationSections
    .filter(section => hasRole(userRoles, section.roles))
    .map(section => ({
      ...section,
      items: section.items.filter(item => hasRole(userRoles, item.roles))
    }))
    .filter(section => section.items.length > 0);

  const lastSectionName = visibleSections[visibleSections.length - 1]?.name;

  return (
    <>
      {/* Overlay for mobile */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 lg:hidden z-40"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:relative inset-y-0 left-0 z-50 sidebar-chrome
          w-64 bg-gray-900
          border-r border-gray-800
          transform transition-transform duration-300 ease-in-out
          ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${!open && 'lg:w-20'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-gray-800">
            {open && (
              <div className="flex items-center gap-3">
                <img src={enactLogo} alt="ENACT Logo" className="h-9 w-auto object-contain" />
              </div>
            )}
            <button
              onClick={() => setOpen(!open)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors hidden lg:block"
            >
              {open ? (
                <ChevronLeft className="w-5 h-5 text-gray-400" />
              ) : (
                <Menu className="w-5 h-5 text-gray-400" />
              )}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors lg:hidden"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Navigation with Sections */}
          <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
            {visibleSections.map((section) => (
              <div key={section.name} className="space-y-1">
                {/* Section Header - only show when sidebar is open */}
                {open && (
                  <button
                    onClick={() => toggleSection(section.name)}
                    className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition-colors"
                  >
                    <span>{section.name}</span>
                    {expandedSections[section.name] ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}

                {/* Section Items */}
                {(open ? expandedSections[section.name] : true) && (
                  <div className="space-y-1">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.name}
                        to={item.path}
                        className={({ isActive }) =>
                          `relative flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group
                          ${
                            isActive
                              ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg shadow-primary-500/30'
                              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                          }
                          ${!open && 'justify-center'}
                          `
                        }
                        title={!open ? item.name : undefined}
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && (
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full bg-[#17edcb]" />
                            )}
                            <item.icon className="w-5 h-5 flex-shrink-0" />
                            {open && (
                              <span className="font-medium">{item.name}</span>
                            )}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}

                {/* Divider between sections */}
                {open && section.name !== lastSectionName && (
                  <div className="border-b border-gray-800/50 mt-3" />
                )}
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="h-14 flex items-center px-4 border-t border-gray-800 flex-shrink-0">
            {open ? (
              <div className="text-xs text-gray-500 leading-tight">
                <p>Version 1.0.0</p>
                <p>© 2024 ENACT Consortium</p>
              </div>
            ) : (
              <p className="text-xs text-gray-500 w-full text-center">v1.0</p>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
